import { faHeart } from "@fortawesome/free-regular-svg-icons";
import { faChevronLeft, faChevronRight, faExclamationTriangle, faHeart as faHeartSolid, faRotate } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Link } from "react-router-dom";
import NameTile from "../../components/NameTile";
import { SortButtons } from "../../components/ui";

const ITEMS_PER_PAGE = 30;

const NamesTable = ({ names, sortColumn, sortDirection, onSort, onBookmark, projectId, maxChars = 0, viewMode = "grid", onRefresh, isRefreshing }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(names.length / ITEMS_PER_PAGE);
  const paginatedNames = names.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getProcess = name => {
    return name.mutations?.at(-1) || name.process || name.model || "-";
  };

  const renderSortIndicator = column => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? "↑" : "↓";
    }
    return null;
  };

  // Grouping helpers for dividers
  const getGroupKey = (item, column) => {
    switch (column) {
      case "name": {
        const firstChar = (item.name || "").charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "#";
      }
      case "trademark":
      case "overall_grade": {
        const grade = item[column];
        return grade ? grade.toUpperCase() : "-";
      }
      case "bookmarked": {
        return item.bookmarked ? "liked" : "not-liked";
      }
      case "model": {
        return item.model || "unknown";
      }
      case "mutation": {
        return item.mutation || "none";
      }
      default:
        return "all";
    }
  };

  const getGroupLabel = (item, column) => {
    switch (column) {
      case "name": {
        const firstChar = (item.name || "").charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "#";
      }
      case "trademark":
      case "overall_grade": {
        const grade = item[column];
        return grade ? grade.toUpperCase() : "No Grade";
      }
      case "bookmarked": {
        return item.bookmarked ? "Liked" : "Not Liked";
      }
      case "model": {
        return item.model || "Unknown Model";
      }
      case "mutation": {
        return item.mutation || "No Mutation";
      }
      default:
        return "";
    }
  };

  const groupNames = (items, column) => {
    const groups = [];
    let currentGroup = null;
    let currentItems = [];

    items.forEach(item => {
      const groupKey = getGroupKey(item, column);
      if (groupKey !== currentGroup) {
        if (currentItems.length > 0) {
          groups.push({ key: currentGroup, label: getGroupLabel(currentItems[0], column), items: currentItems });
        }
        currentGroup = groupKey;
        currentItems = [item];
      } else {
        currentItems.push(item);
      }
    });
    if (currentItems.length > 0) {
      groups.push({ key: currentGroup, label: getGroupLabel(currentItems[0], column), items: currentItems });
    }
    return groups;
  };

  // TILE VIEW
  if (viewMode === "grid") {
    return (
      <div>
        {/* Controls row */}
        <div className="flex items-center justify-between mb-4">
          <SortButtons
            options={[
              { key: "name", label: "Name" },
              { key: "trademark", label: "TM" },
              { key: "overall_grade", label: "Grade" },
              { key: "bookmarked", label: "Liked" },
              { key: "model", label: "Model" },
              { key: "mutation", label: "Mutation" },
            ]}
            value={sortColumn}
            direction={sortDirection}
            onChange={(key, dir) => onSort(key, dir)}
          />
          {/* Refresh button */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="text-base-content/40 hover:text-primary disabled:opacity-50 p-1"
              title="Refresh names"
            >
              <FontAwesomeIcon icon={faRotate} className={isRefreshing ? "animate-spin" : ""} />
            </button>
          )}
        </div>

        {/* Grid with dividers */}
        {groupNames(paginatedNames, sortColumn).map((group, idx) => (
          <div key={group.key} className={idx > 0 ? "mt-6" : ""}>
            <div className="mb-3">
              <span className="text-sm font-medium text-base-content/60">{group.label}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {group.items.map(name => (
                <NameTile
                  key={name.id}
                  name={name}
                  projectId={projectId}
                  onBookmark={onBookmark}
                  maxChars={maxChars}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    );
  }

  // TABLE VIEW
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="table table-xs w-full">
          <thead>
            <tr className="text-xs text-base-content/50">
              <th className="cursor-pointer hover:text-primary" onClick={() => onSort("bookmarked")}>
                Liked {renderSortIndicator("bookmarked")}
              </th>
              <th className="cursor-pointer hover:text-primary" onClick={() => onSort("name")}>
                Name {renderSortIndicator("name")}
              </th>
              <th className="cursor-pointer hover:text-primary" onClick={() => onSort("overall_grade")}>
                Grade {renderSortIndicator("overall_grade")}
              </th>
              <th className="cursor-pointer hover:text-primary" onClick={() => onSort("trademark")}>
                TM {renderSortIndicator("trademark")}
              </th>
              <th className="cursor-pointer hover:text-primary" onClick={() => onSort("process")}>
                Process {renderSortIndicator("process")}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedNames.map(name => (
              <tr key={name.id} className="hover:bg-base-200/50">
                <td>
                  <button onClick={() => onBookmark(name)}>
                    <FontAwesomeIcon
                      icon={name.bookmarked ? faHeartSolid : faHeart}
                      className={name.bookmarked ? "text-primary" : "text-base-content/30"}
                    />
                  </button>
                </td>
                <td>
                  <Link
                    to={`/projects/${projectId}/names/${name.id}`}
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    {name.name}
                    {name.sensitivity && (
                      <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning text-xs" />
                    )}
                  </Link>
                </td>
                <td className="text-base-content/70">{name.overall_grade || "-"}</td>
                <td className="text-base-content/70">{name.trademark || "-"}</td>
                <td>
                  <Link to={`/processes/${getProcess(name)}`} className="text-primary/70 hover:underline text-xs">
                    {getProcess(name)}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
  <div className="flex items-center justify-center gap-4 mt-6">
    <button
      onClick={() => onPageChange(p => Math.max(1, p - 1))}
      disabled={currentPage === 1}
      className="p-2 rounded text-base-content/50 hover:text-base-content disabled:opacity-30"
    >
      <FontAwesomeIcon icon={faChevronLeft} />
    </button>
    <span className="text-sm text-base-content/60">
      {currentPage} of {totalPages}
    </span>
    <button
      onClick={() => onPageChange(p => Math.min(totalPages, p + 1))}
      disabled={currentPage === totalPages}
      className="p-2 rounded text-base-content/50 hover:text-base-content disabled:opacity-30"
    >
      <FontAwesomeIcon icon={faChevronRight} />
    </button>
  </div>
);

export default NamesTable;
